import React from 'react';

const TermsOfService = () => {
  return (
    <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
      <h1>Terms of Service</h1>
      <p><strong>Last Updated: June 13, 2025</strong></p>

      <section>
        <h2>1. Acceptance of Terms</h2>
        
        <h3>A. Age Requirements and Legal Capacity</h3>
        <p>You must be at least 18 years of age to use this service. By accessing or using our board game rules interpretation service (the "Service"), you represent that you are of legal age and are legally capable of entering into these Terms of Service and agree to be bound by all terms and conditions set forth herein.</p>

        <h3>B. Agreement to Terms</h3>
        <p>By using our Service, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and our Privacy Policy, both of which are incorporated herein by reference. If you do not agree to these terms, you must discontinue use of the Service immediately.</p>

        <h3>C. Modifications to Terms</h3>
        <p>We reserve the right to modify these Terms of Service at any time. We will notify users of material changes by posting updates on our website. Continued use of the Service after such modifications constitutes acceptance of the updated terms. We recommend reviewing these terms periodically.</p>
      </section>

      <section>
        <h2>2. Description of Service</h2>
        
        <h3>A. Rules Interpretation Service</h3>
        <p>Our Service provides interpretive assistance and educational guidance regarding board game rules through our website at miskatonic.academy. We offer:</p>
        <ul>
            <li>Rule clarifications and summaries</li>
            <li>Interpretive opinions on complex rule interactions</li>
            <li>Quick reference assistance for gameplay questions</li>
            <li>Educational content to help users better understand game mechanics</li>
        </ul>

        <h3>B. Educational and Interpretive Nature</h3>
        <p>This Service functions similarly to asking an experienced friend for a second opinion while reviewing a rulebook together. We provide interpretive assistance, not authoritative rulings. Our responses are educational in nature and designed to help users understand and apply published rules.</p>

        <h3>C. Commercial Nature of Service</h3>
        <p>While our educational content is provided free of charge to users, we operate as a commercial service supported by advertising revenue, including but not limited to affiliate marketing and display advertising. This commercial support allows us to maintain and improve our free educational offerings.</p>

        <h3>D. No Ownership of Game Content</h3>
        <p>We do not own, claim ownership of, or hold any rights to the board games, rules, artwork, trademarks, or other intellectual property referenced through our Service. All such content remains the exclusive property of the respective publishers and rights holders.</p>

        <h3>E. Anonymous Access</h3>
        <p>Our Service is currently available for anonymous use without requiring account registration. We may provide links to community forums and other websites, including Discord servers, where users can discuss board game rules with other community members. Access to such forums is subject to separate Community Guidelines and the terms of service of the hosting platform (such as Discord's Terms of Service).</p>
      </section>

      <section>
        <h2>3. Advertising and Third-Party Content</h2>
        
        <h3>A. Advertising Revenue Model</h3>
        <p>Our Service displays advertising content to support our free educational offerings. This advertising may include:</p>
        <ul>
            <li>Display advertisements served by third-party advertising networks (such as Google AdSense)</li>
            <li>Affiliate marketing links to board game retailers and publishers</li>
            <li>Sponsored content clearly identified as such</li>
            <li>Product recommendations that may include affiliate compensation</li>
        </ul>

        <h3>B. Editorial Independence</h3>
        <p>Our educational content and rule interpretations are developed independently of our advertising relationships. Advertising revenue does not influence our educational interpretations, recommendations, or the substance of our rules guidance.</p>

        <h3>C. Affiliate Relationship Disclosure</h3>
        <p><strong>Important Disclosure</strong>: We participate in affiliate marketing programs and may earn commissions from purchases made through links on our website. When you click on affiliate links and make purchases, we may receive compensation at no additional cost to you. All affiliate relationships are disclosed in accordance with Federal Trade Commission guidelines.</p>

        <h3>D. Third-Party Advertising Networks</h3>
        <p>We use third-party advertising networks (such as Google AdSense) that may:</p>
        <ul>
            <li>Display targeted advertisements based on your browsing behavior</li>
            <li>Use cookies and similar technologies to collect information about your visits</li>
            <li>Share data with their advertising partners</li>
            <li>Serve advertisements from various advertisers and networks</li>
        </ul>

        <h3>E. Advertiser Content Disclaimer</h3>
        <p>We do not control the content of third-party advertisements or the business practices of advertisers. We are not responsible for:</p>
        <ul>
            <li>The accuracy of advertiser claims</li>
            <li>The quality or availability of advertised products or services</li>
            <li>The privacy practices of advertisers or their websites</li>
            <li>Any transactions between you and advertisers</li>
        </ul>

        <h3>F. User Interaction with Advertisements</h3>
        <p>Your interaction with advertisements, including clicking on links and making purchases, is at your own risk. We encourage you to review the terms of service and privacy policies of any websites you visit through our advertising links.</p>
      </section>

      <section>
        <h2>4. Intellectual Property and Copyright</h2>
        
        <h3>A. Respect for Publisher Rights</h3>
        <p>All board game rules, names, artwork, and related intellectual property referenced through our Service are owned by their respective publishers. We acknowledge and respect these ownership rights and make no claim to such content.</p>

        <h3>B. Fair Use and Educational Purpose</h3>
        <p>Our Service operates under principles of fair use for educational and interpretive purposes. We:</p>
        <ul>
            <li>Do not reproduce copyrighted rules text verbatim</li>
            <li>Provide interpretive summaries and explanations</li>
            <li>Reference rules for educational discussion only</li>
            <li>Encourage users to consult official rulebooks for authoritative information</li>
        </ul>

        <h3>C. Trademark Acknowledgment</h3>
        <p>All game names, publisher names, and related trademarks mentioned on our Service are the property of their respective owners. Use of these names is solely for identification and reference purposes.</p>

        <h3>D. DMCA Notice and Takedown Procedure</h3>
        
        <h4>Copyright Infringement Claims</h4>
        <p>We respect the intellectual property rights of game publishers and other content creators. If you believe that content on our Service infringes your copyright, you may submit a notice pursuant to the Digital Millennium Copyright Act (DMCA).</p>

        <h4>Filing a DMCA Notice</h4>
        <p>To file a DMCA takedown notice, please provide the following information to our designated copyright agent:</p>
        <ol>
            <li><strong>Identification of the copyrighted work</strong> claimed to have been infringed, including the name of the game, publisher, and copyright registration number if available</li>
            <li><strong>Identification of the infringing material</strong> on our Service, including specific URLs or descriptions of where the material appears</li>
            <li><strong>Your contact information</strong>, including name, address, telephone number, and email address</li>
            <li><strong>A statement of good faith belief</strong> that the use of the material is not authorized by the copyright owner, its agent, or the law</li>
            <li><strong>A statement of accuracy</strong> made under penalty of perjury that the information in the notice is accurate and that you are authorized to act on behalf of the copyright owner</li>
            <li><strong>Your physical or electronic signature</strong></li>
        </ol>

        <h4>Copyright Agent Contact Information</h4>
        <p><strong>DMCA Copyright Agent:</strong><br />
        Email: miskatonic.academy@gmail.com<br />
        Subject Line: "DMCA Takedown Notice"</p>

        <h4>Response to Valid Notices</h4>
        <p>Upon receipt of a valid DMCA notice, we will promptly remove or disable access to the allegedly infringing content. We may also restrict access for users who are repeat infringers.</p>

        <h4>Counter-Notification Process</h4>
        <p>If you believe your content was removed due to a mistake or misidentification, you may file a counter-notification with the same contact information provided above. Counter-notifications must include the information required under the DMCA and will be forwarded to the original complainant.</p>

        <h4>Note for Publishers</h4>
        <p>We encourage game publishers to contact us directly at miskatonic.academy@gmail.com before filing formal DMCA notices, as we are committed to working cooperatively with rights holders to address any concerns about our educational use of game content.</p>
      </section>

      <section>
        <h2>5. User Conduct</h2>
        
        <h3>A. Website Service Conduct</h3>
        <p>When using our website service, users must:</p>
        <ul>
            <li>Use the service for its intended educational and interpretive purposes</li>
            <li>Respect intellectual property rights of game publishers</li>
            <li>Provide accurate information when submitting questions or feedback</li>
            <li>Not attempt to exploit, damage, or circumvent the service</li>
            <li>Not interfere with or disrupt advertising content or tracking systems</li>
        </ul>

        <h3>B. Prohibited Activities</h3>
        <p>The following activities are prohibited when using our website service:</p>
        <ul>
            <li>Attempting to reverse engineer or exploit the service</li>
            <li>Using automated tools to access the service without permission</li>
            <li>Submitting false, misleading, or inappropriate content</li>
            <li>Interfering with advertising systems or attempting to manipulate advertising revenue</li>
            <li>Using ad-blocking circumvention techniques to violate our advertising model</li>
            <li>Violating any applicable laws or regulations</li>
            <li>Unauthorized commercial use of our content for competitive purposes</li>
        </ul>

        <h3>C. Advertising Interaction Guidelines</h3>
        <p>Users may interact with advertising content at their discretion but must not:</p>
        <ul>
            <li>Click on advertisements with fraudulent intent</li>
            <li>Attempt to manipulate advertising systems or tracking</li>
            <li>Use automated systems to interact with advertisements</li>
        </ul>
      </section>

      <section>
        <h2>6. User Submissions and Website Content</h2>
        
        <h3>A. User-Generated Content</h3>
        <p>Users may submit questions, comments, and other content related to board game rules through our website. By submitting content, you represent that:</p>
        <ul>
            <li>The content is your own or you have permission to share it</li>
            <li>The content does not violate any third-party rights</li>
            <li>The content complies with these Terms of Service</li>
        </ul>

        <h3>B. License to Use Submissions</h3>
        <p>By submitting content to our Service, you grant us a non-exclusive, royalty-free license to use, display, and distribute your submissions in connection with operating the Service, including in advertising-supported contexts. You retain ownership of your original content.</p>

        <h3>C. Content Moderation</h3>
        <p>We reserve the right to review, modify, or remove user submissions that violate these Terms of Service or are otherwise inappropriate for our educational service. We are not obligated to monitor all user content but may do so at our discretion.</p>

        <h3>D. Anonymous Submissions</h3>
        <p>Since our Service currently operates without user registration, all submissions are anonymous. We cannot associate submissions with specific individuals unless you voluntarily provide identifying information.</p>
      </section>

      <section>
        <h2>7. Third-Party Platforms and Services</h2>
        
        <h3>A. Community Forums</h3>
        <p>We may provide access to or links to community forums hosted on third-party platforms such as Discord. Your use of such platforms is governed by:</p>
        <ul>
            <li>The hosting platform's own terms of service (e.g., Discord Terms of Service)</li>
            <li>Our separate Community Guidelines for forum participation</li>
            <li>Any additional rules established by forum moderators</li>
        </ul>

        <h3>B. Advertising Partners and Networks</h3>
        <p>We work with various third-party advertising partners and networks. These relationships are governed by separate agreements and are subject to the partners' own terms of service and privacy policies.</p>

        <h3>C. Affiliate Partners</h3>
        <p>We maintain affiliate relationships with board game retailers, publishers, and other relevant businesses. These partnerships allow us to earn commissions on qualifying purchases at no additional cost to users.</p>

        <h3>D. No Control Over Third-Party Platforms</h3>
        <p>We do not control third-party platforms, advertising networks, or affiliate partners and are not responsible for their availability, content, policies, or actions.</p>

        <h3>E. Data on Third-Party Platforms</h3>
        <p>Any personal information or content you share on third-party platforms or with advertising partners is subject to those platforms' privacy policies and data handling practices, not ours.</p>
      </section>

      <section>
        <h2>8. Data Collection and Privacy</h2>
        
        <h3>A. Anonymous Usage</h3>
        <p>Our Service currently operates without requiring user registration or the collection of personal information. We may collect anonymous usage data and technical information to improve our Service.</p>

        <h3>B. Cookies and Tracking</h3>
        <p>We use cookies and similar technologies for:</p>
        <ul>
            <li>Website functionality and user experience</li>
            <li>Analytics to understand usage patterns</li>
            <li>Advertising delivery and measurement</li>
        </ul>

        <h3>C. Third-Party Data Collection</h3>
        <p>Our advertising partners and analytics providers may collect data about your visits to our Service in accordance with their own privacy policies. This data collection is separate from our Service operations.</p>

        <h3>D. Future Registration Features</h3>
        <p>If we implement user registration features in the future, we will update these Terms of Service and provide appropriate notice to users before any personal information collection begins.</p>
      </section>

      <section>
        <h2>9. Limitations and Disclaimers</h2>
        
        <h3>A. Service Limitations</h3>
        <p>Our Service is provided "as is" and "as available." We make no warranties, express or implied, regarding the operation of the Service or the information, content, or materials included on the Service.</p>

        <h3>B. No Legal Advice</h3>
        <p>The information provided through our Service is for educational and interpretive purposes only and should not be considered legal advice. Users should consult with appropriate legal counsel for any legal matters.</p>

        <h3>C. No Warranty</h3>
        <p>We do not warrant that the Service will be uninterrupted, timely, secure, or error-free. We do not warrant that the results obtained from the use of the Service will be accurate or reliable.</p>

        <h3>D. Limitation of Liability</h3>
        <p>In no event shall we be liable for any direct, indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of the Service.</p>
      </section>

      <section>
        <h2>10. Contact Information</h2>
        
        <h3>A. General Contact</h3>
        <p>For questions about these Terms of Service or our Service, please contact us at:</p>
        <p>Email: miskatonic.academy@gmail.com</p>

        <h3>B. Legal Notices</h3>
        <p>For legal notices, including DMCA takedown requests, please use the contact information provided in the relevant sections above.</p>
      </section>
    </div>
  );
};

export default TermsOfService;